<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import { Vue } from 'vue-property-decorator';
import type { DataOptions } from 'vuetify';
import {
  DataProductService,
  type GetDataProductResponseDto,
  type AssetSummaryDtoV2,
  type IncidentCatalogAssetDto,
  type DataProductUpstreamIncidentDto,
  IncidentService,
  UserUiDto,
} from '@/api';
import { formatSchedule } from '@/utils/schedule';
import { type GenericEntity } from '@/components/generic-entity/@types/GenericEntityTypes';
import { getModule } from 'vuex-module-decorators';
import authModule from '@/store/modules/auth';
import store from '@/store';
import i18n from '@/i18n';
import SButton from '@/components/SButton/SButton.vue';
import SLabel from '@/components/SLabel.vue';
import SIcon from '@/components/SIcon.vue';
import Tags from '@/components/tags/Tags.vue';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import SRichTextViewer from '@/components/SRichTextViewer.vue';
import DataProductsDrawerDataAssetsTab from '@/modules/data-products/right-drawer/DataProductsDrawerDataAssetsTab.vue';
import DataProductsDrawerTitle from '@/modules/data-products/right-drawer/DataProductsDrawerTitle.vue';
import SNavigationDrawer from '@/components/SNavigationDrawer/SNavigationDrawer.vue';
import SiffletifyDataFooter from '@/components/siffletify/siffletify-data-footer/siffletify-data-footer.vue';
import IncidentsResultsCard from '@/components/incidents/Incidents-Results-Card.vue';
import { composeSortValues } from '@/utils/sort';

type AssetFilterType = 'TABLE_AND_VIEW' | 'DASHBOARD' | 'PIPELINE' | 'GENERIC';
type IncidentsScope = 'DATA_PRODUCT' | 'UPSTREAM';

interface DataProductsDrawerProps {
  item: GenericEntity | null;
  isOpen: boolean;
}

const props = defineProps({
  item: { default: null },
  isOpen: { type: Boolean }
});

interface DataProductsDrawerEmits {
  (event: 'delete-item', id: GenericEntity): void;
  (event: 'update-item', item: GenericEntity): void;
  (event: 'on-cancel'): void;
}
const emits = defineEmits(["delete-item", "update-item", "on-cancel"]);

const DEFAULT_OPTIONS = {
  page: 1,
  itemsPerPage: 25,
};

const DEFAULT_INCIDENTS_OPTIONS = {
  page: 1,
  itemsPerPage: 10,
  sort: ['createdDate,DESC'] as string[],
};

const auth = getModule(authModule, store);
const activeTab = _ref(0);

let dataProduct = _ref<GetDataProductResponseDto | null>(null);
let assetFilterType = _ref<AssetFilterType | undefined>(undefined);
const options = _ref(DEFAULT_OPTIONS);
let assets = _ref<AssetSummaryDtoV2[]>([]);
let assetsCount = _ref<number>(0);

let incidentsScope = _ref<IncidentsScope>('DATA_PRODUCT');
let drawerIncidents = _ref<IncidentCatalogAssetDto[] | DataProductUpstreamIncidentDto[]>([]);
let drawerIncidentsCount = _ref(0);
let drawerIncidentsLoading = _ref(false);
let incidentsIteratorOptions = _ref({
  ...DEFAULT_INCIDENTS_OPTIONS,
  sortBy: ['createdDate'] as string[],
  sortDesc: [true] as boolean[],
});

const canEdit = _computed(() => auth.userActions['data_product.write']);
const canDelete = _computed(() => auth.userActions['data_product.write']);

const name = _computed(() => dataProduct.value?.name || '-');
const description = _computed(() => dataProduct.value?.description || '\\-');
const domainName = _computed(() => dataProduct.value?.domain?.name || '-');
const domainId = _computed(() => dataProduct.value?.domain?.id || '');
const owners = _computed(() => dataProduct.value?.owners || []);
const tags = _computed(() => dataProduct.value?.tags || []);
const sla = _computed(() => (dataProduct.value?.sla ? formatSchedule(dataProduct.value.sla) : '-'));
const isSystemAdmin = _computed(() => auth.getUserRole === UserUiDto.role.ADMIN);

const editDataProduct = () => {
  if (canEdit.value && props.item?.id) {
    emits('update-item', props.item);
  }
};

const deleteDataProduct = () => {
  if (canDelete.value && props.item) {
    emits('delete-item', props.item);
  }
};

const getAssetsLinkedToDataProduct = async () => {
  if (!props.item) return;

  const { id } = props.item;

  const response = await DataProductService.getDataProductAssets({
    dataProductId: id!,
    assetFilterType: assetFilterType.value ? [assetFilterType.value] : undefined,
    page: options.value.page,
    itemsPerPage: options.value.itemsPerPage,
    sort: ['name,ASC'],
  });
  assets.value = response.data || [];
  assetsCount.value = response.filteredElements || 0;
};

const clearDrawerIncidents = () => {
  drawerIncidents.value = [];
  drawerIncidentsCount.value = 0;
};

const fetchOwnedDataProductIncidents = async (product: GetDataProductResponseDto) => {
  const incidentsDomainId = product.domain?.id ?? auth.selectedDomain?.id;
  if (!incidentsDomainId) {
    clearDrawerIncidents();
    return;
  }

  const sort = composeSortValues({
    sortBy: incidentsIteratorOptions.value.sortBy,
    sortDesc: incidentsIteratorOptions.value.sortDesc,
  });
  const { searchIncidents } = await IncidentService.getAllIncidentsV2({
    requestBody: {
      domain: incidentsDomainId,
      status: ['OPEN', 'IN_PROGRESS'],
      page: incidentsIteratorOptions.value.page,
      itemsPerPage: incidentsIteratorOptions.value.itemsPerPage,
      sort: sort.length ? sort : DEFAULT_INCIDENTS_OPTIONS.sort,
      dataProduct: [product.id],
    },
  });
  drawerIncidents.value = searchIncidents.data ?? [];
  drawerIncidentsCount.value = searchIncidents.totalElements ?? 0;
};

const fetchDataProductUpstreamIncidents = async (dataProductId: string) => {
  const { data, totalElements } = await DataProductService.getDataProductUpstreamIncidents({
    id: dataProductId,
    page: incidentsIteratorOptions.value.page,
    itemsPerPage: incidentsIteratorOptions.value.itemsPerPage,
  });
  drawerIncidents.value = data ?? [];
  drawerIncidentsCount.value = totalElements ?? 0;
};

const fetchDataProductIncidents = async () => {
  const product = dataProduct.value;
  const isUpstream = incidentsScope.value === 'UPSTREAM';
  if (!product?.id) {
    clearDrawerIncidents();
    return;
  }

  drawerIncidentsLoading.value = true;
  try {
    if (isUpstream) {
      await fetchDataProductUpstreamIncidents(product.id);
    } else {
      await fetchOwnedDataProductIncidents(product);
    }
  } catch {
    clearDrawerIncidents();
    Vue.notify({
      type: 'error',
      text: i18n.t('data_products.right_drawer.incidents_load_error') as string,
    });
  } finally {
    drawerIncidentsLoading.value = false;
  }
};

const updateIncidentsIteratorOptions = (opts: Partial<DataOptions>) => {
  const { itemsPerPage, sortBy, sortDesc } = opts;
  const sort = composeSortValues({ sortBy, sortDesc });
  const page = opts.itemsPerPage !== incidentsIteratorOptions.value.itemsPerPage
    ? 1
    : (opts.page ?? incidentsIteratorOptions.value.page);
  incidentsIteratorOptions.value = {
    ...incidentsIteratorOptions.value,
    page,
    itemsPerPage: itemsPerPage ?? incidentsIteratorOptions.value.itemsPerPage,
    sortBy: sortBy ?? incidentsIteratorOptions.value.sortBy,
    sortDesc: sortDesc ?? incidentsIteratorOptions.value.sortDesc,
    sort: sort.length ? sort : DEFAULT_INCIDENTS_OPTIONS.sort,
  };
};

const handleUpdateFilter = (newAssetFilterType: AssetFilterType | undefined) => {
  assetFilterType.value = newAssetFilterType;
  getAssetsLinkedToDataProduct();
};

const handleCloseDrawer = () => emits('on-cancel');

const initializeDrawer = async () => {
  if (!props.item) return;
  const { id } = props.item;
  dataProduct.value = await DataProductService.getDataProduct({ id: id! });
};

const routeToDomain = (id: string) => ({
  name: 'domains.domain.edit',
  params: { id },
});

watch(() => incidentsScope.value, () => {
  incidentsIteratorOptions.value = { ...incidentsIteratorOptions.value, page: 1 };
});

watch(() => options.value, () => {
  getAssetsLinkedToDataProduct();
});

watch(
  () => props.item,
  async (newItem: GenericEntity | null, oldItem: GenericEntity | null | undefined) => {
    if (!newItem) return;
    const idChanged = newItem.id !== oldItem?.id;
    if (idChanged) {
      options.value.page = DEFAULT_OPTIONS.page;
      options.value.itemsPerPage = DEFAULT_OPTIONS.itemsPerPage;
      assetFilterType.value = undefined;
      incidentsIteratorOptions.value = {
        ...DEFAULT_INCIDENTS_OPTIONS,
        sortBy: ['createdDate'],
        sortDesc: [true],
      };
      incidentsScope.value = 'DATA_PRODUCT';
    }
    await initializeDrawer();
    await getAssetsLinkedToDataProduct();
  },
  { immediate: true },
);

watch(
  () => ({
    itemId: props.item?.id,
    dataProductId: dataProduct.value?.id,
    scope: incidentsScope.value,
    page: incidentsIteratorOptions.value.page,
    itemsPerPage: incidentsIteratorOptions.value.itemsPerPage,
    sortKey: `${(incidentsIteratorOptions.value.sortBy ?? []).join(',')}:${(incidentsIteratorOptions.value.sortDesc ?? []).join(',')}`,
  }),
  async () => {
    if (!props.item?.id || dataProduct.value?.id !== props.item.id) return;
    await fetchDataProductIncidents();
  },
  { immediate: true },
);
</script>

<template lang="pug">
SNavigationDrawer(
  :is-open="isOpen"
  @close-drawer="handleCloseDrawer"
  data-track="data-products-drawer-navigation"
)

  template(#title)
    DataProductsDrawerTitle(
      v-if="dataProduct"
      :name="name"
      :status="dataProduct.dataProductStatus"
      :upstream-status="dataProduct.dataProductUpstreamStatus"
    )

  template(#content)
    .v-container.pt-0(data-track="data-products-drawer")
      v-row(no-gutters)
        v-col
          SButton.mr-3(
            v-if='canEdit'
            icon="$edit"
            variant="outlined"
            color="secondary"
            :text="$t('common.words.edit')"
            @click='editDataProduct'
            data-track="data-products-drawer-button-edit"
          )

          SButton(
            v-if="canDelete"
            icon="$delete"
            variant="outlined"
            color="danger"
            :text="$t('common.words.delete')"
            @click="deleteDataProduct"
            data-track="data-products-drawer-button-delete"
          )

      v-row.mt-4(no-gutters)
        v-col
          v-tabs(v-model="activeTab" data-track="data-products-drawer-tabs")
            v-tab {{ $t('data_products.right_drawer.tab_overview') }}
            v-tab {{ $t('data_products.right_drawer.tab_data_assets') }}
            v-tab {{ $t('data_products.right_drawer.tab_incidents') }}

          v-divider

      v-row(no-gutters)
        v-col
          v-tabs-items(v-model="activeTab")
            // - Overview Tab
            v-tab-item
              .mt-4
                SLabel {{ $t('data_products.right_drawer.label_sla') }}
                  template(#input)
                    span {{ sla }}
              .mt-4
                SLabel {{ $t('data_products.right_drawer.label_description') }}
                  template(#input)
                    SRichTextViewer.viewer(:content="description")

              .mt-4
                SLabel {{ $t('data_products.right_drawer.label_tags') }}
                  template(#input)
                    Tags(
                      v-if="tags.length"
                      :tags="tags"
                      :small="true"
                      :outlined="true"
                      :color="'primary'"
                    )
                    span(v-else class="grey--text") -

              .mt-4
                SLabel {{ $t('data_products.right_drawer.label_domain') }}
                  template(#input)
                    .d-flex.align-center
                      SIcon.mr-1( size="small" icon='icon-building')
                      RouterLink.text-decoration-none(:to="routeToDomain(domainId)" v-if="isSystemAdmin" ) {{ domainName }}
                      span.text-truncate(v-else) {{ domainName }}

              .mt-4
                SLabel {{ $t('data_products.right_drawer.label_owners') }}
                  template(#input)
                    SAvatars(
                      :users="owners"
                      wrapped
                      size="20"
                    )
            // - Data Assets Tab
            v-tab-item
              .mt-4
                DataProductsDrawerDataAssetsTab(
                  v-model="options"
                  :assets="assets"
                  :assets-count="assetsCount"
                  @update-filter="handleUpdateFilter"
                )

            // - Incidents Tab
            v-tab-item
              .mt-4
                v-btn-toggle.mb-4(
                  v-model="incidentsScope"
                  dense
                  mandatory
                  data-track="data-products-drawer-incidents-scope"
                )
                  v-btn.text-body-2.custom-secondary(
                    color="secondary"
                    text
                    outlined
                    value="DATA_PRODUCT"
                    data-track="data-products-drawer-incidents-scope-data-product"
                  ) {{ $t('data_products.right_drawer.incidents_scope_data_product') }}
                  v-btn.text-body-2.custom-secondary(
                    color="secondary"
                    text
                    outlined
                    value="UPSTREAM"
                    data-track="data-products-drawer-incidents-scope-upstream"
                  ) {{ $t('data_products.right_drawer.incidents_scope_upstream') }}
                v-row(no-gutters)
                  v-col
                    v-data-iterator(
                      :items="drawerIncidents"
                      :loading="drawerIncidentsLoading"
                      :server-items-length="drawerIncidentsCount"
                      :options.sync="incidentsIteratorOptions"
                      hide-default-footer
                      data-track="data-products-drawer-incidents-iterator"
                    )
                      template(v-slot:loading)
                        MonitorsResultsPlaceholder

                      template(v-slot:no-data)
                        NoDataAvailablePlaceholder(
                          :text="$t('no_data.no_incidents_found')"
                          icon="icon-flag"
                        )

                      template(v-slot:item="itemProps")
                        IncidentsResultsCard(
                          :item-props="itemProps"
                          :selectable="false"
                        )

                      template(v-slot:footer="iteratorFooter")
                        //- Pass slot scope as footer model (options + pagination). Same reference as VDataIterator; no :footer.sync — ESLint disallows .sync on slot vars, and this component syncs via @update only.
                        SiffletifyDataFooter(
                          :footer="iteratorFooter"
                          :maximum-visible="5"
                          :items-per-page-options="[10, 25, 50, 100]"
                          @update="updateIncidentsIteratorOptions($event)"
                        )
</template>

<style lang="scss" scoped>
.viewer {
  max-height: 400px;
}

.v-btn-toggle {
  .v-btn.v-btn--has-bg {
    background-color: var(--v-bgPrimary-base) !important;
  }
}
</style>
